import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import {
  useDisruptionProgress,
  useResolutionState,
} from '@/lib/disruptions/use-disruptions';
import type { DisruptionDetail, ResolutionOp } from '@/lib/disruptions/types';
import { DisruptionPulseBridge } from './disruption-pulse-bridge';
import { DisruptionSmartHero } from './disruption-smart-hero';
import { DisruptionPipelineStage } from './disruption-pipeline-stage';
import { DisruptionInlineExceptionDeck } from './disruption-inline-exception-deck';
import { DisruptionFlightMatrix } from './disruption-flight-matrix';

interface DisruptionCommandCenterV2Props {
  disruptionUrn: string;
  detail: DisruptionDetail;
}

export function DisruptionCommandCenterV2({
  disruptionUrn,
  detail,
}: DisruptionCommandCenterV2Props) {
  const { t } = useTranslation('tower');
  const qc = useQueryClient();
  const { data, isLoading, isError } = useResolutionState(disruptionUrn);
  const progress = useDisruptionProgress(disruptionUrn);
  const [selectedOp, setSelectedOp] = useState<ResolutionOp | null>(null);

  // WS-driven refresh (same seam as standard overview)
  const p = progress.data;
  const g = p?.groups && 'groups' in p.groups ? p.groups : null;
  const fingerprint = p
    ? [
        p.runStatus,
        p.processedGroups ?? 0,
        p.roomsCommitted ?? 0,
        p.resolved,
        p.optionsGenerated,
        p.activeRun?.op ?? '',
        p.activeRun?.runId ?? '',
        p.activeRun?.progress?.done ?? '',
        p.allocationRun?.status ?? '',
        p.allocationRun?.processedGroups ?? '',
        p.allocationRun?.heldGroups ?? '',
        g ? `${g.assigned}:${g.ticketed}:${g.hotelHeld}:${g.hotelConfirmed}:${g.voucherIssued}:${g.awaiting}:${g.accepted}:${g.declined}:${g.resolved}` : '',
      ].join('|')
    : null;

  useEffect(() => {
    if (!fingerprint) return;
    void qc.invalidateQueries({ queryKey: ['disruptions', disruptionUrn, 'resolution-state'] });
  }, [fingerprint, disruptionUrn, qc]);

  if (isError) {
    return (
      <EmptyState title={t('alt.error.title')} description={t('alt.error.description')} />
    );
  }

  if (isLoading || !data) {
    return (
      <div className="flex flex-col gap-4 p-2" data-testid="command-center-loading">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-24 w-full rounded-xl" />
        </div>
        <Skeleton className="h-36 w-full rounded-xl" />
        <Skeleton className="h-44 w-full rounded-xl" />
      </div>
    );
  }

  const isEmpty = data.kpis.caseCount === 0 && data.kpis.affectedPassengers === 0;

  if (isEmpty) {
    return (
      <EmptyState title={t('alt.empty.title')} description={t('alt.empty.description')} />
    );
  }

  return (
    <div className="flex flex-col gap-4 overflow-y-auto pr-1 pb-6" data-testid="disruption-command-center-v2">
      {/* 1. Situational Telemetry & Protection Pulse */}
      <DisruptionPulseBridge 
        detail={detail} 
        state={data} 
        live={progress.data} 
      />

      {/* 2. Intelligent Next Best Action (Hero Card) */}
      <DisruptionSmartHero 
        disruptionUrn={disruptionUrn} 
        state={data} 
      />

      {/* 3. Interactive 6-Stage Resolution Pipeline */}
      <DisruptionPipelineStage 
        disruptionUrn={disruptionUrn} 
        state={data}
        selectedOp={selectedOp}
        onSelectOp={setSelectedOp}
      />

      {/* 4. Inline Exception Radar & Quick Fix Resolver */}
      <DisruptionInlineExceptionDeck 
        state={data} 
        disruptionUrn={disruptionUrn} 
      />

      {/* 5. Affected Flights & Airport Scope Matrix */}
      <DisruptionFlightMatrix 
        detail={detail} 
      />
    </div>
  );
}
